import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { API_PATHS } from "../../api/apiPaths";
import { http } from "../../api/httpClient";
import { getMobileAuthRequestConfig } from "../../api/mobileRequestConfig";
import { useUtm } from "../../context/UtmContext";
import SelectAccountScreen, { getAccountId } from "../requirements/SelectAccountScreen";
import {
  extractLinkedAccounts,
  persistAuthTokenFromVerifyResponse,
  persistLoggedInUser,
} from "../shared/mobileAuthUtils";
import "../requirements/requirements.css";
import "./createBusiness.css";
import {
  trackCreateBusinessStart,
  trackCreateBusinessStep,
  trackCreateBusinessSuccess,
} from "../../utils/analytics";

const ALLOWED_CREATE_USER_KEYS = new Set([
  "phoneNumber",
  "username",
  "password",
  "accountType",
  "businessName",
  "firstName",
  "email",
  "lastName",
  "address",
  "locationServed",
  "minBudget",
  "maxBudget",
  "professionalType",
  "professionalCategory",
  "categoryId",
  "categoryIdArray",
  "subCategoryIds",
  "servicesProvided",
  "otherServices",
  "website",
  "socialMedia",
  "bio",
  "aboutUs",
  "businessEmail",
  "location",
  "activeSince",
  "teamSize",
  "dateOfBirth",
  "gender",
  "category",
  "certifications",
  "awards",
  "achievements",
  "profilePic",
  "businessOwnerId",
  "isContentCreator",
  "userPlan",
  "planStartDate",
  "planEndDate",
  "planDuration",
  "planStatus",
  "isPaid",
  "source",
  "utmSource",
]);

const pickAllowedCreateUserPayload = (input) =>
  Object.fromEntries(
    Object.entries(input).filter(
      ([key, value]) =>
        ALLOWED_CREATE_USER_KEYS.has(key) &&
        value !== undefined &&
        value !== null &&
        !(typeof value === "string" && value.trim() === ""),
    ),
  );

function CreateBusinessFlow() {
  const navigate = useNavigate();
  const location = useLocation();
  const { utmSource } = useUtm();
  const createUserDraft = location?.state?.createUserDraft || null;
  const categoryId = location?.state?.categoryId || createUserDraft?.categoryId || "";
  const [step, setStep] = useState("mobile");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [isOtpFocused, setIsOtpFocused] = useState(false);
  const [otpOrderId, setOtpOrderId] = useState("");
  const [linkedAccounts, setLinkedAccounts] = useState([]);
  const [isRequestingOtp, setIsRequestingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isSelectingAccount, setIsSelectingAccount] = useState(false);
  const otpInputRef = useRef(null);
  const otpSubmittedRef = useRef(false);

  useEffect(() => {
    trackCreateBusinessStart({ buttonName: "Enter mobile number" });
  }, []);

  const isPhoneValid = /^[0-9]{10}$/.test(phoneNumber);
  const otpValue = useMemo(() => otp.join(""), [otp]);
  const isOtpValid = otpValue.length === 6;
  const activeOtpIndex = Math.min(otpValue.length, 5);

  const handlePhoneChange = (event) => {
    if (otpOrderId) setOtpOrderId("");
    setPhoneNumber(event.target.value.replace(/\D/g, "").slice(0, 10));
  };

  const buildSendOtpPayload = () => ({
    mobile: `91${phoneNumber}`,
    phoneNumber: `91${phoneNumber}`,
  });

  const buildVerifyOtpPayload = () => ({
    phoneNumber: `91${phoneNumber}`,
    otp: otpValue,
    orderId: otpOrderId,
    request_id: otpOrderId,
    cid: categoryId,
    categoryId,
  });

  const continueToCategory = () => navigate("/create-business/category");

  const handleContinueToOtp = async () => {
    if (!isPhoneValid) return;
    trackCreateBusinessStep({ step: 1, buttonName: "Send OTP" });
    setIsRequestingOtp(true);
    try {
      const { data } = await http.post(
        API_PATHS.SEND_LOGIN_OTP,
        buildSendOtpPayload(),
        getMobileAuthRequestConfig(),
      );
      const resolvedOrderId =
        data?.request_id || data?.data?.request_id || data?.result?.request_id || "";
      if (!resolvedOrderId) {
        throw new Error("OTP session missing from response. Please try again.");
      }
      setOtpOrderId(String(resolvedOrderId));
    } catch (error) {
      const message =
        error?.response?.data?.message || error?.message || "Could not send OTP. Try again.";
      alert(message);
      return;
    } finally {
      setIsRequestingOtp(false);
    }

    setStep("otp");
    setOtp(["", "", "", "", "", ""]);
    window.setTimeout(() => otpInputRef.current?.focus(), 0);
  };

  const handleOtpChange = (event) => {
    const nextValue = event.target.value.replace(/\D/g, "").slice(0, 6);
    const nextOtp = nextValue.split("");
    while (nextOtp.length < 6) nextOtp.push("");
    setOtp(nextOtp);
  };

  const handleBack = () => {
    if (step === "accounts") {
      setStep("otp");
      otpSubmittedRef.current = false;
      return;
    }
    if (step === "otp") {
      setStep("mobile");
      otpSubmittedRef.current = false;
      return;
    }
    navigate(-1);
  };

  const handleVerifyOtp = async () => {
    if (!isOtpValid) return;
    if (otpSubmittedRef.current) return;
    otpSubmittedRef.current = true;
    if (!otpOrderId) {
      alert("OTP session expired. Please send OTP again.");
      setStep("mobile");
      otpSubmittedRef.current = false;
      return;
    }
    setIsVerifyingOtp(true);
    try {
      const verifyResponse = await http.post(
        API_PATHS.VERIFY_LOGIN_OTP,
        buildVerifyOtpPayload(),
        getMobileAuthRequestConfig(),
      );
      persistAuthTokenFromVerifyResponse(verifyResponse?.data);
      const accounts = extractLinkedAccounts(verifyResponse?.data);
      setLinkedAccounts(accounts);
      setStep("accounts");
    } catch (error) {
      const message =
        error?.response?.data?.message || error?.message || "OTP verification failed.";
      alert(message);
      otpSubmittedRef.current = false;
      setOtp(["", "", "", "", "", ""]);
      window.setTimeout(() => otpInputRef.current?.focus(), 0);
      return;
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleCreateAccount = async () => {
    if (isSelectingAccount) return;
    if (!createUserDraft) {
      alert("Please complete category and profile details first.");
      navigate("/create-business/category");
      return;
    }
    if (!isPhoneValid) {
      alert("Please enter a valid phone number.");
      setStep("mobile");
      return;
    }

    setIsSelectingAccount(true);
    try {
      const payload = pickAllowedCreateUserPayload({
        ...createUserDraft,
        phoneNumber,
        utmSource,
      });
      await http.post(API_PATHS.CREATE_USER, payload, getMobileAuthRequestConfig());
      trackCreateBusinessSuccess({ buttonName: "Create new account" });
      navigate("/create-business/success", { state: { createUserDraft } });
    } catch (error) {
      const message =
        error?.response?.data?.message || error?.message || "Could not create profile.";
      alert(message);
    } finally {
      setIsSelectingAccount(false);
    }
  };

  const handleSelectAccount = async (account) => {
    const accountId = getAccountId(account);
    if (!accountId || isSelectingAccount) return;

    setIsSelectingAccount(true);
    try {
      const { data } = await http.post(
        API_PATHS.GENERATE_AUTH_TOKEN,
        { userDetails: accountId },
        getMobileAuthRequestConfig(),
      );
      if (data?.status && data.status !== 200) {
        throw new Error(data?.message || "Could not continue with this profile.");
      }
      const user = data?.user || data?.data?.user || account;
      const token =
        data?.authToken ||
        data?.token ||
        data?.data?.authToken ||
        data?.data?.token ||
        "";
      if (!token) {
        throw new Error("Login token missing. Please try again.");
      }
      persistLoggedInUser(user, token);
      navigate("/create-business/success", { replace: true });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Could not continue with this profile.";
      alert(message);
    } finally {
      setIsSelectingAccount(false);
    }
  };

  useEffect(() => {
    if (step !== "otp") return;
    if (!isOtpValid || isVerifyingOtp) return;
    handleVerifyOtp();
  }, [isOtpValid, isVerifyingOtp, step]);

  if (step === "accounts") {
    return (
      <div className="requirements-page create-business-accounts-page">
        <div className="requirements-shell">
          <SelectAccountScreen
            accounts={linkedAccounts}
            phoneNumber={phoneNumber}
            onSelectAccount={handleSelectAccount}
            onCreateNewAccount={handleCreateAccount}
            onBack={handleBack}
            isLoading={isSelectingAccount}
            eyebrowText="Create business account"
            selectLabel="Select a profile to continue"
            continueLabel="Continue"
            loadingLabel="Continuing..."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="create-business-page">
      <div className="create-business-shell">
        <main className="create-business-main">
          {step === "mobile" ? (
            <>
              <h1 className="create-business-title">Enter your mobile number</h1>
              <p className="create-business-subtitle">
                We'll send you a one-time code to verify.
              </p>

              <div className="create-business-phone-input-wrap">
                <span className="create-business-country-code">+91</span>
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  className="create-business-phone-input"
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  placeholder="Phone number"
                />
              </div>
            </>
          ) : (
            <>
              <h1 className="create-business-title">Enter OTP</h1>
              <p className="create-business-subtitle create-business-subtitle-otp">
                OTP sent to +91 {phoneNumber}{" "}
                <button
                  type="button"
                  className="create-business-change-btn"
                  onClick={() => {
                    setStep("mobile");
                    otpSubmittedRef.current = false;
                  }}
                >
                  Change
                </button>
              </p>

              <div className="create-business-otp-wrap">
                <input
                  ref={otpInputRef}
                  type="tel"
                  inputMode="numeric"
                  maxLength={6}
                  className="create-business-otp-input-hidden"
                  value={otpValue}
                  onChange={handleOtpChange}
                  onFocus={() => setIsOtpFocused(true)}
                  onBlur={() => setIsOtpFocused(false)}
                  aria-label="Enter 6 digit OTP"
                />
                {otp.map((digit, index) => (
                  <div
                    key={index}
                    className={`create-business-otp-cell ${
                      isOtpFocused && index === activeOtpIndex ? "is-active" : ""
                    }`}
                  >
                    {digit ? <span className="create-business-otp-digit">{digit}</span> : null}
                    {!digit && isOtpFocused && index === activeOtpIndex ? (
                      <span className="create-business-otp-caret" />
                    ) : null}
                    {!digit && !(isOtpFocused && index === activeOtpIndex) ? (
                      <span className="create-business-otp-dot" />
                    ) : null}
                  </div>
                ))}
              </div>
            </>
          )}

          {step === "mobile" ? (
            <p className="create-business-terms">
              By continuing, you agree to our <Link to="/terms">Terms of Service</Link>,{" "}
              <Link to="/privacy-policy">Privacy Policy</Link> and{" "}
              <Link to="/cookie-policy">Cookie Policy</Link>, and confirm that you are at least
              18 years old.
            </p>
          ) : null}
        </main>

        <footer className="create-business-footer">
          <button type="button" className="create-business-back-btn" onClick={handleBack}>
            Go back
          </button>
          <button
            type="button"
            className="create-business-primary-btn"
            onClick={step === "mobile" ? handleContinueToOtp : handleVerifyOtp}
            disabled={
              step === "mobile"
                ? !isPhoneValid || isRequestingOtp
                : !isOtpValid || isVerifyingOtp
            }
          >
            {step === "mobile"
              ? isRequestingOtp
                ? "Sending..."
                : "Send OTP"
              : isVerifyingOtp
                ? "Verifying..."
                : "Verify"}
          </button>
        </footer>
      </div>
    </div>
  );
}

export default CreateBusinessFlow;
